import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { s } from '@/theme/tokens';
import { Box, Button, Panel, Stack, Text } from '@/ui';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { Section } from '@/ui/layout/Section';
import { PageShell } from '@/ui/primitives/PageShell';
import type { ReactElement } from 'react';

type NotFoundScreenProps = {
  title?: string;
  body?: string;
  backTo?: string;
  backLabel?: string;
};

const toTitleCaseWords = (value: string): string =>
  value.replace(/\b([a-z])/g, (match) => match.toUpperCase());

export const NotFoundScreen = ({
  title = 'Page Not Found',
  body = 'The page you are looking for does not exist. Check the URL or head back.',
  backTo = '/',
  backLabel = 'Go Back',
}: NotFoundScreenProps): ReactElement => {
  const { setOverrideVisible, clearOverride } = useBreadcrumbs();

  useEffect(() => {
    setOverrideVisible(false);
    return () => {
      clearOverride();
    };
  }, [clearOverride, setOverrideVisible]);

  return (
    <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack gap="xl" sx={{ pt: s('5xl') }}>
        <Section>
          <Box sx={{ width: '100%' }}>
            <Panel>
              <Stack
                gap="lg"
                sx={{
                  width: '100%',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <Text role="pageTitle">{title}</Text>
                <Text role="bodyMuted">{body}</Text>
                <Button component={RouterLink} to={backTo} variant="primary">
                  {toTitleCaseWords(backLabel)}
                </Button>
              </Stack>
            </Panel>
          </Box>
        </Section>
      </Stack>
    </PageShell>
  );
};
